import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/** Где стоит выбранный элемент внутри блока — в координатах содержимого блока. */
export interface Slide {
  top: number;
  bottom: number;
  height: number;
  /** Сдвинулся вниз (или встал впервые). От этого зависит, какой край едет первым. */
  down: boolean;
  /** Переехал на другой элемент. При пересчёте по размеру подсветка не едет, а встаёт сразу. */
  moved: boolean;
}

/**
 * Место выбранного элемента для подсветки, которая не появляется на новом
 * месте, а едет туда, — так в макете v6: линза канала и засечка сервера
 * скользят по столбцу.
 *
 * Место — по раскладке (`placeOf`), а не по прямоугольнику на экране: в
 * прямоугольник входит `transform`, а нажатая строка сжимается (`:active`).
 * Выбор приходит, пока она ещё разжимается, и следом за ним идёт пересчёт по
 * наблюдателю — прямоугольник к тому времени сдвигался на доли точки, это
 * читалось как «список поменялся», и подсветка вставала на место без езды.
 *
 * Пересчитывается при смене `key` (выбрали другое) и при изменении размера
 * блока (свернули категорию, пришёл новый канал) — во втором случае без
 * езды, сразу на место.
 */
/**
 * Место строки в содержимом блока без `transform`. Сумма `offsetTop` по цепочке
 * до блока — между ними бывают свои позиционированные обёртки, и одного
 * `offsetTop` мало. Не дошли до блока (он не точка отсчёта) — по
 * прямоугольникам, с прокруткой самого блока.
 */
function placeOf(el: HTMLElement, box: HTMLElement): { top: number; height: number } {
  let top = 0;
  let node: HTMLElement | null = el;
  while (node && node !== box) {
    top += node.offsetTop;
    const parent = node.offsetParent as HTMLElement | null;
    if (parent && parent !== box) top += parent.clientTop;
    node = parent;
  }
  if (node === box) return { top, height: el.offsetHeight };

  const b = box.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return { top: r.top - b.top + box.scrollTop, height: r.height };
}

export function useSlide(root: RefObject<HTMLElement | null>, selector: string, key: unknown): Slide | null {
  const [slide, setSlide] = useState<Slide | null>(null);
  const last = useRef<Slide | null>(null);

  useLayoutEffect(() => {
    const box = root.current;
    if (!box) return;

    const place = (moved: boolean) => {
      const el = box.querySelector<HTMLElement>(selector);
      const was = last.current;
      if (!el) {
        last.current = null;
        if (was) setSlide(null);
        return;
      }
      const { top, height } = placeOf(el, box);
      const bottom = box.scrollHeight - top - height;
      /*
       * Ничего не сдвинулось — состояние не трогаем. Иначе наблюдатель,
       * пришедший следом за выбором, переписал бы «едет» на «встань сразу»
       * ещё до отрисовки, и подсветка прыгала бы без пути.
       */
      if (was && was.top === top && was.bottom === bottom && was.height === height) return;
      const next = {
        top,
        bottom,
        height,
        down: !was || top >= was.top,
        moved: moved && !!was,
      };
      last.current = next;
      setSlide(next);
    };

    place(true);
    /*
     * Размер блока ловит свёрнутую категорию, а смену содержимого — приход
     * списка: у ряда серверов высота задана снаружи, и появление значков
     * размера не меняет.
     */
    const ro = new ResizeObserver(() => place(false));
    ro.observe(box);
    const mo = new MutationObserver(() => place(false));
    mo.observe(box, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [root, selector, key]);

  return slide;
}
